import { useState, type ReactElement } from 'react';
import { useDispatchRunEvent } from '../api/runs';
import { isTerminalRunState } from '../lib/runState';
import { Modal } from './Modal';
import { ActionButton } from './ActionButton';
import { InlineError } from './InlineError';
import { useToast } from './toastContext';

// Cancel a non-terminal run (A1EE-338). Used by RunHeader and the RunsList row.
// Confirms in a Modal (never window.confirm); no reason. Cancellation is
// Run-owned (A1EE-238) — the cascade to the Job happens server-side.
export function CancelRunButton({
  jobId,
  runId,
  runState,
  size = 'md',
}: {
  jobId: string;
  runId: string;
  runState: string;
  size?: 'sm' | 'md';
}): ReactElement | null {
  const dispatch = useDispatchRunEvent(jobId, runId);
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);

  if (isTerminalRunState(runState)) return null;

  const confirm = () => {
    dispatch.mutate(
      { event: 'Cancel' },
      {
        onSuccess: () => {
          showToast('Run cancelled', 'success');
          setOpen(false);
        },
      },
    );
  };

  const triggerClass =
    size === 'sm'
      ? 'rounded border border-red-300 bg-white px-2 py-0.5 text-xs font-semibold text-red-800 hover:border-red-400'
      : 'rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-800 hover:border-red-400';

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={triggerClass}>
        Cancel run
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Cancel run"
        footer={
          <>
            <ActionButton onClick={() => setOpen(false)} variant="neutral">
              Keep running
            </ActionButton>
            <ActionButton
              onClick={confirm}
              variant="danger"
              pending={dispatch.isPending}
              pendingLabel="Cancelling…"
            >
              Confirm cancel
            </ActionButton>
          </>
        }
      >
        <div className="space-y-3">
          {dispatch.isError && (
            <InlineError heading="Couldn't cancel the run" error={dispatch.error} />
          )}
          <p className="text-sm text-slate-700">
            This tears down the sandbox and marks the run <strong>CANCELLED</strong>. This
            can&apos;t be undone.
          </p>
        </div>
      </Modal>
    </>
  );
}
